import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, useLive } from './live';

/**
 * Load rows with a Supabase query. `deps` re-run it (e.g. when the site changes).
 * `live` = tables to watch: any change by anyone reloads the list quietly (no "Loading…" flash).
 * `liveFilter` narrows what counts as a change (Realtime filter, e.g. "linked_id=eq.<uuid>").
 */
export function useRows<T>(
  run: () => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
  deps: unknown[],
  live: string[] = [],
  liveFilter?: string,
) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const first = useRef(true);

  const reload = useCallback(async () => {
    if (first.current) setLoading(true);
    const { data, error } = await run();
    setRows(data ?? []);
    setError(error ? error.message : null);
    setLoading(false);
    first.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    first.current = true;
    void reload();
  }, [reload]);

  const quiet = useMemo(() => debounce(() => void reload()), [reload]);
  useLive(live, quiet, { filter: liveFilter });

  return { rows, loading, error, reload, setRows };
}
